import React, { useState, useEffect, useRef } from "react"
import styled, { css } from "styled-components"
import { graphql, useStaticQuery } from "gatsby"

import useStickyElement from "../../../hooks/useStickyElement"

import { minD, maxD } from "../../../assets/styles/helpers/devices"
import { PageWithoutScroll } from "../../../assets/styles/helpers/modificators"

import { Wrapper, WideWrapper } from "../common/containers"
import { Button } from "../atoms/Button"
import { UnderlinedLink } from "../common/links"
import { HamburgerMenu } from "./Hamburger"
import { Brand } from "./Brand"

import EnglishNavList from "./list/EnglishNavList"
import PolishNavList from "./list/PolishNavList"

import SiteLanguage from "../../../helpers/siteLanguage"

import Icon from "./../../../assets/images/svg/phone.svg"

import LanguageSwitcher from "./../../language-switcher/language-switcher"
import { telHref } from "../../../helpers/analytics"
import imageSize from "../../../helpers/imageSize"
import optimizedPath from "../../../helpers/optimizedPath"

const ACFQuery = graphql`
  {
    wp {
      pageHeader {
        en {
          phoneText
          phoneNumber
          contactLink {
            url
            title
            target
          }
        }
        pl {
          phoneTextPl
          phoneNumberPl
          contactLinkPl {
            url
            title
            target
          }
        }
      }
      topBar {
        en {
          topBarContent
          topBarLink {
            url
            title
            target
          }
          topBarImage {
            sourceUrl
            altText
            mediaDetails {
              width
              height
            }
          }
          topBarBackground {
            sourceUrl
          }
          topBarContentMobile
          topBarLinkMobile {
            url
            title
            target
          }
          topBarImageMobile {
            sourceUrl
            altText
            mediaDetails {
              width
              height
            }
          }
          topBarBackgroundMobile {
            sourceUrl
          }
        }
        pl {
          topBarContentPl
          topBarLinkPl {
            url
            title
            target
          }
          topBarImagePl {
            sourceUrl
            altText
            mediaDetails {
              width
              height
            }
          }
          topBarBackgroundPl {
            sourceUrl
          }
          topBarContentMobilePl
          topBarLinkMobilePl {
            url
            title
            target
          }
          topBarImageMobilePl {
            sourceUrl
            altText
            mediaDetails {
              width
              height
            }
          }
          topBarBackgroundMobilePl {
            sourceUrl
          }
        }
      }
    }
  }
`

const NavInner = styled.nav`
  position: fixed;
  width: 100vw;
  padding: 45px 0 0;
  top: 0;

  z-index: 9980;

  transition: padding 400ms ease;

  ${props => props.hasMobileBar && css`
    @media ${maxD.tablet} {
      padding-top: calc(90px + var(--bar-extra, 0px));
    }
  `}
  ${props => props.hasDesktopBar && css`
    @media ${minD.tablet} {
      padding-top: calc(90px + var(--bar-extra, 0px));
    }
  `}

  &::after {
    display: block;
    position: absolute;
    content: "";

    width: 100%;
    height: 100%;
    z-index: -1;

    top: 0;
    left: 0;
    background-color: #fff;

    transform: translateY(-180px);
    transition: transform 1500ms var(--fancy-cubic-bezier);
    box-shadow: rgb(0 0 0 / 4%) 0px 0px 1px, rgb(0 0 0 / 6%) 0px 0px 2.9px,
      rgb(0 0 0 / 7%) 0px 0px 6.9px, rgb(0 0 0 / 11%) 0px 0px 23px;
  }

  &.sticky {
    padding: 0.5rem 0;
    z-index: 9999;

    &::after {
      transform: translateY(0);
    }

    @media ${maxD.mobileL} {
      padding: 1rem 0;
    }
  }
`

const NavButtons = styled.div`
  display: flex;
  align-items: center;
`

const NavPhone = styled(WideWrapper)`
  position: absolute;
  top: 10px;
  left: 0;
  right: 0;
  padding-right: 0;
  z-index: 9990;
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 2rem;

  transition: top 400ms ease;

  ${props => props.hasMobileBar && css`
    @media ${maxD.tablet} {
      top: calc(55px + var(--bar-extra, 0px));
    }
  `}
  ${props => props.hasDesktopBar && css`
    @media ${minD.tablet} {
      top: calc(55px + var(--bar-extra, 0px));
    }
  `}
`
const AwardBar = styled.a`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  min-height: 45px;
  z-index: 9990;
  display: flex;
  justify-content: end;
  align-items: center;
  background-color: #000;
  ${props => props.bgImage && `background-image: url(${props.bgImage});`}
  background-size: cover;
  background-position: center;

  ${props => props.hideOnMobile && css`
    @media ${maxD.tablet} {
      display: none;
    }
  `}
  ${props => props.hideOnDesktop && css`
    @media ${minD.tablet} {
      display: none;
    }
  `}
`
const AwardContainer = styled(Wrapper)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  padding-top: 8px;
  padding-bottom: 8px;

  @media ${maxD.tablet} {
    justify-content: center;
    gap: 10px;
  }

  p {
    color: #FFF;
    font-size: 12px;
    font-weight: 400;
    margin: 0;

    @media ${maxD.mobileL} {
      font-size: 10px;
    }
  }

  img {
    width: auto;
    height: 40px;
    flex-shrink: 0;

    @media ${maxD.tablet} {
      height: 26px;
    }
  }
`

const DesktopLanguageSwitcher = styled.div`
  @media ${maxD.tabletL} {
    display: none;
  }
`

const PhoneLine = styled.p`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: 10px;
  font-size: 0.85rem;
`

const PhoneIcon = styled(Icon)`
  width: 1rem;
  height: 1rem;
  max-width: 18px;
  max-height: 18px;
  margin-right: 1rem;

  fill: var(--main-green);
`

const useElementHeight = enabled => {
  const ref = useRef(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    if (!enabled || !ref.current || typeof ResizeObserver === "undefined") {
      setHeight(0)
      return
    }

    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.contentRect.height)
    })
    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [enabled])

  return [ref, height]
}

const Nav = ({ pageContext, data }) => {
  const { isSticky } = useStickyElement()
  const [isMobileActive, toggleNavActivity] = useState(false)
  // let link = ''


  // I know, this way of doing multilingualism is not the best, but I was doing it 5 hours before leaving the company :c

  const acfData = useStaticQuery(ACFQuery)
  const enData = acfData.wp.pageHeader.en
  const plData = acfData.wp.pageHeader.pl
  const topBarEnData = acfData.wp.topBar.en
  const topBarPlData = acfData.wp.topBar.pl

  let phoneText, phoneNumber, contactLink, link
  let topBarContent, topBarLink, topBarImage, topBarBackground
  let topBarContentMobile, topBarLinkMobile, topBarImageMobile, topBarBackgroundMobile

  if (SiteLanguage() === "en") {
    phoneText = enData.phoneText
    phoneNumber = enData.phoneNumber
    contactLink = enData.contactLink
    link = "https://bravenew.agency/"

    topBarContent = topBarEnData.topBarContent
    topBarLink = topBarEnData.topBarLink
    topBarImage = topBarEnData.topBarImage
    topBarBackground = topBarEnData.topBarBackground

    topBarContentMobile = topBarEnData.topBarContentMobile
    topBarLinkMobile = topBarEnData.topBarLinkMobile
    topBarImageMobile = topBarEnData.topBarImageMobile
    topBarBackgroundMobile = topBarEnData.topBarBackgroundMobile
  } else {
    phoneText = plData.phoneTextPl
    phoneNumber = plData.phoneNumberPl
    contactLink = plData.contactLinkPl
    link = "https://bravenew.agency/pl/"

    topBarContent = topBarPlData.topBarContentPl
    topBarLink = topBarPlData.topBarLinkPl
    topBarImage = topBarPlData.topBarImagePl
    topBarBackground = topBarPlData.topBarBackgroundPl

    topBarContentMobile = topBarPlData.topBarContentMobilePl
    topBarLinkMobile = topBarPlData.topBarLinkMobilePl
    topBarImageMobile = topBarPlData.topBarImageMobilePl
    topBarBackgroundMobile = topBarPlData.topBarBackgroundMobilePl
  }

  const hasDesktopBar = Boolean(topBarContent)
  const hasMobileBar = Boolean(topBarContentMobile)

  const [desktopBarRef, desktopBarHeight] = useElementHeight(hasDesktopBar)
  const [mobileBarRef, mobileBarHeight] = useElementHeight(hasMobileBar)
  const barExtra = Math.max(0, desktopBarHeight - 45, mobileBarHeight - 45)
  const cssVars = { "--bar-extra": `${barExtra}px` }

  const isNavCollapsed = isSticky || isMobileActive

  return (
    <>
      {isMobileActive && <PageWithoutScroll />}
      <NavInner
        className={isNavCollapsed && "sticky"}
        hasDesktopBar={hasDesktopBar}
        hasMobileBar={hasMobileBar}
        style={cssVars}
      >
        <WideWrapper flex spaceBetween alignItems="c">
          <Brand url={ link } />
          {SiteLanguage() === "en" ? (
            <EnglishNavList isActive={isMobileActive} pageContext={pageContext} />
          ) : (
            <PolishNavList isActive={isMobileActive} pageContext={pageContext} />
          )}
          <NavButtons>
            {contactLink && (
              <Button
                theme="orange"
                to={contactLink.url}
                target={contactLink.target || "_self"}
                style={{ fontSize: "0.8rem" }}
              >
                {contactLink.title}
              </Button>
            )}
            <HamburgerMenu
              handleHamburgerClick={() => toggleNavActivity(!isMobileActive)}
              isActive={isMobileActive}
            />
          </NavButtons>
        </WideWrapper>
      </NavInner>
      {hasDesktopBar && (
        <AwardBar
          ref={desktopBarRef}
          hideOnMobile
          target="_blank"
          rel="nofollow, noopener"
          href={topBarLink?.url}
          bgImage={topBarBackground?.sourceUrl && optimizedPath(topBarBackground.sourceUrl)}
        >
          <AwardContainer>
            <div dangerouslySetInnerHTML={{ __html: topBarContent }} />
            {topBarImage?.sourceUrl && (
              <img src={optimizedPath(topBarImage.sourceUrl)} alt={topBarImage.altText || ""} {...imageSize(topBarImage)} />
            )}
          </AwardContainer>
        </AwardBar>
      )}
      {hasMobileBar && (
        <AwardBar
          ref={mobileBarRef}
          hideOnDesktop
          target="_blank"
          rel="nofollow, noopener"
          href={topBarLinkMobile?.url}
          bgImage={topBarBackgroundMobile?.sourceUrl && optimizedPath(topBarBackgroundMobile.sourceUrl)}
        >
          <AwardContainer>
            <div dangerouslySetInnerHTML={{ __html: topBarContentMobile }} />
            {topBarImageMobile?.sourceUrl && (
              <img src={optimizedPath(topBarImageMobile.sourceUrl)} alt={topBarImageMobile.altText || ""} {...imageSize(topBarImageMobile)} />
            )}
          </AwardContainer>
        </AwardBar>
      )}
      <NavPhone relative hasDesktopBar={hasDesktopBar} hasMobileBar={hasMobileBar} style={cssVars}>
        <DesktopLanguageSwitcher>
          <LanguageSwitcher pageContext={pageContext} />
        </DesktopLanguageSwitcher>
        <PhoneLine>
          <PhoneIcon />
          {phoneText} &nbsp;
          {phoneNumber && (
            <UnderlinedLink
              href={telHref(phoneNumber)}
              style={{ fontSize: "0.85rem" }}
              as="a"
            >
              {phoneNumber}
            </UnderlinedLink>
          )}
        </PhoneLine>
      </NavPhone>
    </>
  )
}

export default Nav
